// กล่องรับเพชร — landing-ver2: "แคปซูลนีออน" ติดขอบขวากลางจอ (ตัวเลขชม./นาที/วิ ซ้อนแนวตั้ง + แถบความคืบหน้าข้างตัว)
// กดแล้วเลื่อนลิ้นชักจากขวา: แท็บ "กล่องรับเพชร" (ค่าที่แอดมินตั้งใน /wheeling) + แท็บ "ประวัติ" เป็นไทม์ไลน์แยกตามวัน
import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import {
  DIAMOND_OPEN,
  describeInterval,
  fmtClockTime,
  groupByDay,
  sourceMeta,
  splitHMS,
  useDiamondBox,
  useDiamondBoxEnabled,
  useDiamondHistory,
} from "./diamondLogic";

const p2 = (n) => String(n).padStart(2, "0");

function Timeline({ hist }) {
  const { items, loading, loaded, hasMore, summary, loadMore } = hist;
  const groups = groupByDay(items);
  return (
    <div className="dx-hist">
      <div className="dx-stats">
        <div>
          <span>ได้รับสะสม</span>
          <b>{(summary?.totalReceived ?? 0).toLocaleString()}</b>
        </div>
        <div>
          <span>จำนวนครั้ง</span>
          <b>{(summary?.count ?? 0).toLocaleString()}</b>
        </div>
      </div>

      {!loaded && loading && <div className="dx-empty">กำลังโหลด...</div>}
      {loaded && items.length === 0 && (
        <div className="dx-empty">
          <Icon icon="fluent-emoji-flat:gem-stone" width={44} />
          <p>ยังไม่มีประวัติเพชร</p>
          <small>เพชรที่ได้รับจะแสดงที่นี่ พร้อมบอกว่ามาจากไหน</small>
        </div>
      )}

      {groups.map((g) => (
        <section key={g.label} className="dx-day">
          <h4>{g.label}</h4>
          <ol>
            {g.items.map((it) => {
              const meta = sourceMeta(it.source);
              return (
                <li key={it.id} style={{ "--tone": meta.tone }}>
                  <span className="dx-node">
                    <Icon icon={meta.icon} width={22} />
                  </span>
                  <div className="dx-ev">
                    <div className="dx-ev-top">
                      <b>{meta.label}</b>
                      <em>+{it.amount.toLocaleString()}</em>
                    </div>
                    <div className="dx-ev-sub">
                      <span>{it.note || "ได้รับเพชร"}</span>
                      <span>
                        {fmtClockTime(it.created_at)}
                        {it.balance_after !== null ? ` · คงเหลือ ${it.balance_after.toLocaleString()}` : ""}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}

      {hasMore && (
        <button type="button" className="dx-more" onClick={loadMore} disabled={loading}>
          {loading ? "กำลังโหลด..." : "โหลดเพิ่ม"}
        </button>
      )}
    </div>
  );
}

function BoxTab({ box, goHistory, onLogin, onRegister }) {
  const { guest, ready, remaining, progress, claiming, claim, pointValue, minutes } = box;
  const { h, m, s } = splitHMS(remaining);
  return (
    <div className="dx-box">
      <div className={`dx-clock${ready ? " ready" : ""}`}>
        {guest ? (
          <div className="dx-ready dx-guest">
            <Icon icon="fluent-emoji-flat:locked" width={34} />
            <b>เข้าสู่ระบบเพื่อรับเพชรฟรี</b>
            <span>รับ +{pointValue.toLocaleString()} เพชร ทุก {describeInterval(minutes)}</span>
          </div>
        ) : ready ? (
          <div className="dx-ready">
            <Icon icon="fluent-emoji-flat:sparkles" width={34} />
            <b>พร้อมรับแล้ว!</b>
            <span>+{pointValue.toLocaleString()} เพชร</span>
          </div>
        ) : (
          <div className="dx-tiles" aria-label="เวลาที่เหลือ">
            {[
              [h, "ชม."],
              [m, "นาที"],
              [s, "วินาที"],
            ].map(([v, l], i) => (
              <React.Fragment key={l}>
                {i > 0 && <span className="dx-colon">:</span>}
                <div className="dx-tile">
                  <b>{p2(v)}</b>
                  <span>{l}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
        {!guest && (
          <div className="dx-meter" aria-hidden="true">
            <i style={{ width: `${progress * 100}%` }} />
          </div>
        )}
      </div>

      <div className="dx-tiles2">
        <div>
          <Icon icon="fluent-emoji-flat:gem-stone" width={30} />
          <span>เพชรต่อกล่อง</span>
          <b>+{pointValue.toLocaleString()}</b>
        </div>
        <div>
          <Icon icon="fluent-emoji-flat:alarm-clock" width={30} />
          <span>รอบนับถอยหลัง</span>
          <b>{describeInterval(minutes)}</b>
        </div>
      </div>

      {guest ? (
        <>
          <button type="button" className="dx-claim" onClick={onLogin}>
            <Icon icon="solar:login-3-bold-duotone" width={22} />
            <span>เข้าสู่ระบบเพื่อรับเพชร</span>
          </button>
          <button type="button" className="dx-link" onClick={onRegister}>
            ยังไม่มีบัญชี? สมัครสมาชิกฟรี <Icon icon="solar:alt-arrow-right-linear" width={16} />
          </button>
        </>
      ) : (
        <>
          <button type="button" className="dx-claim" onClick={claim} disabled={!ready || claiming}>
            <Icon icon={ready ? "fluent-emoji-flat:wrapped-gift" : "fluent-emoji-flat:locked"} width={24} />
            <span>{claiming ? "กำลังรับ..." : ready ? "รับเพชรเลย" : "ยังไม่ถึงเวลา"}</span>
          </button>

          <button type="button" className="dx-link" onClick={goHistory}>
            ดูประวัติเพชรที่ได้รับ <Icon icon="solar:alt-arrow-right-linear" width={16} />
          </button>
        </>
      )}
    </div>
  );
}

export default function DiamondBox({ uid }) {
  const nav = useNavigate();
  const enabled = useDiamondBoxEnabled();
  const box = useDiamondBox(uid);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("box");
  const hist = useDiamondHistory(uid, open);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onOpen = (e) => {
      setTab(e.detail?.tab === "history" ? "history" : "box");
      setOpen(true);
    };
    window.addEventListener(DIAMOND_OPEN, onOpen);
    return () => window.removeEventListener(DIAMOND_OPEN, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!enabled || !box.loaded) return null;

  const { guest } = box;
  const { h, m, s } = splitHMS(box.remaining);
  const goAuth = (path) => () => {
    close();
    nav(path);
  };

  return (
    <>
      <button
        type="button"
        className={`dx-dock${box.ready ? " ready" : ""}${guest ? " guest" : ""}${open ? " open" : ""}`}
        onClick={() => {
          setTab("box");
          setOpen((o) => !o);
        }}
        aria-label="กล่องรับเพชร"
        aria-expanded={open}
      >
        <span className="dx-bar" aria-hidden="true">
          <i style={{ height: `${box.progress * 100}%` }} />
        </span>
        <Icon icon="fluent-emoji-flat:gem-stone" width={30} className="dx-gem" />
        {guest || box.ready ? (
          <span className="dx-plus">+{box.pointValue}</span>
        ) : (
          <span className="dx-digits" aria-label="เวลาที่เหลือ">
            <b>{p2(h)}</b>
            <b>{p2(m)}</b>
            <b>{p2(s)}</b>
          </span>
        )}
        <span className="dx-lbl">{box.ready && !guest ? "กดรับ" : "เพชรฟรี"}</span>
      </button>

      {open && (
        <>
          <div className="dx-backdrop" onClick={close} />
          <aside className="dx-drawer" role="dialog" aria-modal="true" aria-label="กล่องเพชรฟรี">
            <header className="dx-head">
              <div>
                <span className="dx-kicker">DIAMOND BOX</span>
                <h3>กล่องเพชรฟรี</h3>
              </div>
              <button type="button" className="dx-x" onClick={close} aria-label="ปิด" autoFocus>
                <Icon icon="solar:close-square-bold-duotone" width={28} />
              </button>
            </header>

            {!guest && (
            <div className="dx-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={tab === "box"} className={tab === "box" ? "on" : ""} onClick={() => setTab("box")}>
                <Icon icon="fluent-emoji-flat:wrapped-gift" width={18} /> กล่องรับเพชร
              </button>
              <button type="button" role="tab" aria-selected={tab === "history"} className={tab === "history" ? "on" : ""} onClick={() => setTab("history")}>
                <Icon icon="solar:history-bold-duotone" width={18} /> ประวัติ
              </button>
            </div>
            )}

            <div className="dx-body">
              {tab === "box" || guest ? (
                <BoxTab box={box} goHistory={() => setTab("history")} onLogin={goAuth("/auth")} onRegister={goAuth("/auth?mode=register")} />
              ) : (
                <Timeline hist={hist} />
              )}
            </div>
          </aside>
        </>
      )}
    </>
  );
}
